import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import user from "@/store/modules/user";
import offices from "@/store/modules/offices";
import orders from "@/store/modules/orders/orders";
import filters from "@/store/modules/filters";
import { createDirectStore } from "direct-vuex";
import AppSettings from "@/models/api/AppSettings";
import {
  getAppSettings,
  getFiscalPrinters,
  getFiscalRegisters,
  getTaskTypeList,
  getServiceTypes,
  getChangeReasons,
} from "@/requests/general";
import orderAddEdit from "@/store/modules/orders/orderAddEdit/orderAddEdit";
import bookingAddEdit from "@/store/modules/bookings/bookingAddEdit/bookingAddEdit";
import bookings from "@/store/modules/bookings/bookings";
import popup from "@/store/modules/popup";
import documents from "@/store/modules/documents/documents";
import finances from "@/store/modules/finances/finances";
import cashiersAndTraders from "@/store/modules/cashiersAndTraders";
import serviceTypes from "@/store/modules/serviceTypes";
import SelectItem from "@/models/components/inputs/selectItem";
import attributes from "@/store/modules/attributes";
import costDocuments from "@/store/modules/costDocuments/costDocuments";
import exchangeRate from "@/store/modules/exchangeRate";
import administration from "@/store/modules/administration/administration";
import mails from "@/store/modules/mails";
import { waitAsync } from "@/utils/wait";
import payments from "@/store/modules/payments";
import ContractorsModule from "@/store/modules/contractors";
import { Printer } from "@/models/api/Printer";
import { ServiceType } from "@/models/api/serviceTypes/ServiceType";
import bookkeeping from "@/store/modules/bookkeeping/bookkeeping";
import cashboxes from "@/store/modules/cashboxes";
import bankAccounts from "@/store/modules/bankAccounts";
import reports from "@/store/modules/reports/reports";
import touristModule from "@/store/modules/touristModule/touristModule";
import statistics from "@/store/modules/statistics";
import SelectItemDisabled from "@/models/components/inputs/selectItemDisabled";
import developer from "@/store/modules/developer";
import costDocumentsOther from "@/store/modules/costDocumentsOther/costDocumentsOther";
import { AccountingChangeReason } from "@/models/api/changeReason/ChangeReason";
import { getGDSList } from "@/requests/administration/administrationGDS";
import contractorDialog from "@/store/modules/contractorDialog";
import userPersistedData from "@/store/modules/userPersistedData";
import bookingReports from "@/store/modules/reports/bookingReports";
import recordStamp from "@/store/modules/recordStamp";
import contracts from "@/store/modules/contracts/contracts";
import buffer from "@/store/modules/buffer";
Vue.use(Vuex);

export interface RootStateInterface {
  appSettings?: AppSettings;
  ajaxPendingConnections: number;
  dialogCount: number;
  GDS: null | Array<SelectItemDisabled>;
  fiscalRegisters: null | Array<SelectItem>;
  fiscalPrinters: null | Array<Printer>;
  isWaitingForRequestsDialog: boolean;
  taskTypes: null | Array<SelectItem>;
  serviceTypeList: null | Array<ServiceType>;
  accountingChangeReasons: AccountingChangeReason[];
}

export const rootState: RootStateInterface = {
  appSettings: undefined,
  ajaxPendingConnections: 0,
  dialogCount: 0,
  GDS: null,
  fiscalRegisters: null,
  fiscalPrinters: null,
  isWaitingForRequestsDialog: false,
  taskTypes: null,
  serviceTypeList: null,
  accountingChangeReasons: [],
};

export const makeSetters = (state: unknown): unknown => {
  const result: Record<string, unknown> = {};
  console.log(state);
  for (const [key] of Object.entries(state as Record<string, unknown>)) {
    result[key] = (state: Record<string, unknown>, param: unknown) => {
      state[key] = param;
    };
  }
  console.log(result);
  return result;
};

const { store, rootActionContext, moduleActionContext, rootGetterContext, moduleGetterContext } = createDirectStore({
  state: rootState,
  modules: {
    administration: administration,
    exchangeRate,
    user,
    offices,
    orders,
    orderAddEdit,
    filters,
    bookings,
    popup,
    bookingAddEdit,
    documents,
    finances,
    cashiersAndTraders,
    serviceTypes,
    attributes,
    costDocuments,
    costDocumentsOther,
    mails,
    payments,
    cashboxes,
    contractors: ContractorsModule,
    bookkeeping: bookkeeping,
    bankAccounts,
    reports: reports,
    bookingReports,
    touristModule,
    statistics,
    developer,
    contractorDialog,
    userPersistedData,
    recordStamp,
    contracts,
    buffer,
  },
  mutations: {
    RAISE_PENDING_CALLS(state) {
      state.ajaxPendingConnections++;
    },
    LOWER_PENDING_CALLS(state) {
      state.ajaxPendingConnections--;
    },
    RAISE_DIALOG_COUNT(state) {
      state.dialogCount++;
    },
    LOWER_DIALOG_COUNT(state) {
      state.dialogCount--;
    },
    SET_APP_SETTINGS(state, appSettings: AppSettings) {
      state.appSettings = appSettings;
    },
    SET_GDS_LIST(state, param: Array<SelectItemDisabled>) {
      state.GDS = param.sort((a, b) => a.text.localeCompare(b.text));
    },
    SET_FISCAL_REGISTERS(state, param: null | Array<SelectItem>) {
      state.fiscalRegisters = param;
    },
    SET_IS_WAITING_FOR_REQUESTS_DIALOG(state, param: boolean) {
      state.isWaitingForRequestsDialog = param;
    },
    SET_FISCAL_PRINTERS(state, param: null | Array<Printer>) {
      state.fiscalPrinters = param;
    },
    SET_TASK_TYPE(state, param: null | Array<SelectItem>) {
      state.taskTypes = param;
    },
    SET_SERVICE_TYPES(state, param: null | Array<ServiceType>) {
      state.serviceTypeList = param;
    },
    SET_CHANGE_REASONS(state, param: AccountingChangeReason[]) {
      state.accountingChangeReasons = param;
    },
  },
  getters: {
    isLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.ajaxPendingConnections > 0;
    },
  },
  actions: {
    async downloadAppSettings(context) {
      const { commit } = rootActionContext(context);
      commit.SET_APP_SETTINGS(await getAppSettings());
    },
    async downloadGDS(context) {
      const { state, commit } = rootActionContext(context);
      if (state.GDS === null) {
        const result = await getGDSList();
        const selectArray = new Array<SelectItemDisabled>();
        for (const item of result) {
          selectArray.push({
            text: item.translatedName,
            value: item.id,
            disabled: !item.isActive,
          });
        }
        commit.SET_GDS_LIST(selectArray);
      }
    },
    async downloadFiscalRegisters(context) {
      const { state, commit } = rootActionContext(context);
      if (state.fiscalRegisters === null) {
        const result = await getFiscalRegisters();
        const selectArray = new Array<SelectItem>();
        for (const item of result) {
          selectArray.push({ text: item.displayRegisterName, value: item.id });
        }
        commit.SET_FISCAL_REGISTERS(selectArray);
      }
    },
    async downloadFiscalPrinters(context) {
      const { commit } = rootActionContext(context);
      const result = await getFiscalPrinters();
      commit.SET_FISCAL_PRINTERS(result);
    },
    async downloadTaskType(context) {
      const { state, commit } = rootActionContext(context);
      if (state.taskTypes === null) {
        const result = await getTaskTypeList();
        const selectArray = new Array<SelectItem>();
        for (const item of result) {
          selectArray.push({ text: item.typeName, value: item.id });
        }
        commit.SET_TASK_TYPE(selectArray);
      }
    },
    async downloadServiceTypes(context) {
      const { state, commit } = rootActionContext(context);
      if (state.serviceTypeList === null) {
        const result = await getServiceTypes();
        commit.SET_SERVICE_TYPES(result);
      }
    },
    async downloadChangeReasons(context) {
      const { state, commit } = rootActionContext(context);
      if (state.accountingChangeReasons.length === 0) {
        const result = await getChangeReasons();
        commit.SET_CHANGE_REASONS(result);
      }
    },
    async waitForCompleteAllRequests(context): Promise<void> {
      const { state, commit } = rootActionContext(context);
      while (state.ajaxPendingConnections > 0) {
        commit.SET_IS_WAITING_FOR_REQUESTS_DIALOG(true);
        await waitAsync(150);
      }
      commit.SET_IS_WAITING_FOR_REQUESTS_DIALOG(false);
    },
    failIfRequestsInProgress(context): void {
      const { dispatch, state } = rootActionContext(context);
      if (state.ajaxPendingConnections > 0) {
        dispatch.popup.addWaitTillOperationsCompleteSnackbar();
        throw new Error("Another operation is in progress");
      }
    },
  },
  plugins: [
    createPersistedState({
      paths: [
        "appSettings",
        "offices.allOffices",
        "user.tokens",
        "user.preserveFiltersState",
        "user.showDocumentsAfterGeneration",
        "user.userOfficeProfiles",
        "user.uncheckBookingAfterExit",
        "contractorDialog",
        "userPersistedData",
      ],
    }),
  ],
  // … store implementation here …
});

export { store as vuexStore };
export default store;

// The following exports will be used to enable types in the
// implementation of actions and getters.
export { rootActionContext, moduleActionContext, rootGetterContext, moduleGetterContext };

// The following lines enable types in the injected store '$store'.
export type AppStore = typeof store;
declare module "vuex" {
  //eslint-disable-next-line
  interface Store<S> {
    direct: AppStore;
  }
}

/* eslint-disable */
// @ts-ignore
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, store);
